// MissionTiles.js — home page: vision + focus areas + solutions preview + products + CTA
import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  ShieldCheck,
  Leaf,
  HeartHandshake,
  Database,
  Shield,
  Users,
  ArrowUpRight,
  Brain,
  Footprints,
  Lightbulb,
} from 'lucide-react';
import './MissionTiles.css';

const vision = [
  {
    icon: <Brain />,
    title: 'Thinking',
    desc: 'A sustainable mind comes first. Positive, patient thinking is what produces sustainable action in the world.',
  },
  {
    icon: <Footprints />,
    title: 'Actions',
    desc: 'Doing things safely and responsibly — in technology, in lifestyle, and in how we use the environment and natural resources.',
  },
  {
    icon: <Lightbulb />,
    title: 'Innovations',
    desc: 'Building with attention to the future — weighing the longer-term impact of what we create, not just its immediate output.',
  },
];

const missions = [
  {
    icon: <BarChart3 />,
    title: 'Organizational Sustainability',
    desc: 'Drive long-term success with data strategy built for durability, not quarters.',
  },
  {
    icon: <ShieldCheck />,
    title: 'Trust & Compliance',
    desc: 'Ensure robust data governance and earn lasting public trust.',
  },
  {
    icon: <Leaf />,
    title: 'Sustainable Ecosystems',
    desc: 'Power ESG goals with reporting, analytics, and forecasting tools.',
  },
  {
    icon: <HeartHandshake />,
    title: 'Human-Centric Innovation',
    desc: 'Put people first in AI and system design — empathy before automation.',
  },
];

const previews = [
  {
    icon: <Database />,
    title: 'Enterprise Data Strategy',
    desc: 'Align data priorities to long-term business goals.',
  },
  {
    icon: <Shield />,
    title: 'Data Security & Governance',
    desc: 'Build trust and transparency with modern MDM and governance.',
  },
  {
    icon: <Leaf />,
    title: 'ESG Reporting',
    desc: 'From ESG data marts to BI, analytics, and forecasting.',
  },
  {
    icon: <Users />,
    title: 'Customer Experience Solutions',
    desc: 'Sentiment, NPS drivers, and feedback intelligence.',
  },
];

const MissionTiles = () => {
  return (
    <>
      {/* Vision */}
      <section className="vision-band">
        <div className="wrap">
          <span className="eyebrow">Our vision</span>
          <h2 className="section-title">Sustainability, in everything.</h2>
          <p className="vision-lede">
            Our vision is not just about data. Sustune exists to bring
            sustainability into thinking, actions, and innovations — in
            technology, in lifestyle, in how we use the environment and
            natural resources — doing things safely, with attention to the
            longer-term impact of our actions.
          </p>
          <div className="vision-grid">
            {vision.map((v) => (
              <div key={v.title} className="vision-tile">
                <span className="vision-icon">{v.icon}</span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus areas */}
      <section className="focus-band">
        <div className="wrap">
          <span className="eyebrow">Our focus areas</span>
          <h2 className="section-title">Four commitments, one direction.</h2>
          <div className="focus-grid">
            {missions.map((m) => (
              <div key={m.title} className="focus-tile">
                <div className="focus-icon">{m.icon}</div>
                <h3>{m.title}</h3>
                <p>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions preview — dark band */}
      <section className="solutions-band on-dark">
        <div className="wrap">
          <span className="eyebrow">What we do</span>
          <h2 className="section-title">Solutions that hold up.</h2>
          <div className="preview-list">
            {previews.map((p) => (
              <Link to="/solutions" key={p.title} className="preview-row">
                <span className="preview-icon">{p.icon}</span>
                <span className="preview-text">
                  <strong>{p.title}</strong>
                  <span>{p.desc}</span>
                </span>
                <ArrowUpRight className="preview-arrow" size={20} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Products — GudWords */}
      <section className="products-band">
        <div className="wrap">
          <span className="eyebrow">From our studio</span>
          <div className="product-card">
            <div className="product-quote" aria-hidden="true">
              &ldquo;
            </div>
            <div className="product-text">
              <h2>GudWords</h2>
              <p className="product-desc">
                A collection of great sayings from across the world's cultures
                and languages — to inspire people and bring positive thinking.
              </p>
              <p className="product-why">
                Because a sustainable human mind is where sustainable actions
                begin.
              </p>
              <a
                className="btn btn-solid"
                href="https://gudwords.org"
                target="_blank"
                rel="noreferrer"
              >
                Visit GudWords <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Grounding + CTA */}
      <section className="home-cta">
        <div className="wrap home-cta-inner">
          <p className="home-cta-note">
            Registered in Bengaluru, serving global organizations through our
            partner ecosystem.
          </p>
          <h2>Ready to transform sustainably?</h2>
          <a
            className="btn btn-solid"
            href="mailto:info@sustunetech.com?subject=Consultation%20Enquiry"
          >
            Schedule a consultation
          </a>
        </div>
      </section>
    </>
  );
};

export default MissionTiles;
